import { z } from "zod";
import { SimulationObjectiveSchema } from "./simulations.js";
import { SimulationPeriodSchema } from "./simulations-run.js";
import { type CommandSpec, Uuid } from "./assistant-catalog-types.js";

// Família Simulação (action · GESTOR). Cada comando reusa o service da tela via dispatch.
export const SIMULATION_COMMANDS: readonly CommandSpec[] = [
  {
    name: "scenario.preview",
    kind: "action",
    riskTier: "safe", // cliente auto-confirma sem card; efêmero (não persiste/audita)
    minRole: "GESTOR",
    description:
      "Calcula o preview leve (analítico) de um cenário num período — resultado imediato.",
    argsSchema: z.object({ scenarioId: Uuid, period: SimulationPeriodSchema }).strict(),
  },
  {
    name: "scenario.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cria um novo cenário de simulação (a partir de uma linha base ou linha nova).",
    // `productId` VOLTOU a ser consumido: com linha + produto o cenário nasce com
    // `structureSnap` e é executável na hora. Sem ele, o cenário criado pelo assistente ficava sem
    // estrutura e o lote seguinte morria com "Cenário sem estrutura válida para otimizar".
    // (Havia sido removido em 2026-07-14, quando o create de fato o descartava.)
    argsSchema: z
      .object({
        name: z.string().min(1).max(120),
        baseLineId: Uuid.optional(),
        productId: Uuid.optional(),
      })
      .strict(),
  },
  {
    name: "simulation.run",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Executa a simulação de um cenário num período (rodada detalhada, persistida).",
    // A janela de calibração é `period.calibrationDays` (único knob) — o duplicado top-level era
    // morto (o dispatch só encaminha {period, isBaseline}; removido em 2026-07-14).
    argsSchema: z
      .object({
        scenarioId: Uuid,
        period: SimulationPeriodSchema,
        isBaseline: z.boolean().optional(),
      })
      .strict(),
  },
  {
    name: "simulation.suggestImprovements",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Gera um lote de candidatos otimizando um objetivo (Sugerir melhorias).",
    // `targetQuantity`/`productionOrderId` fazem parte do contrato porque o dispatcher
    // re-valida com `CreateAutoBatchRequestSchema`, que os EXIGE conforme o objetivo. Sem eles no
    // schema `.strict()`, escolher TIME_TO_QUANTITY ou OP_ON_TIME era um beco: o modelo não podia
    // enviá-los, a re-pergunta não disparava (nada faltava no schema do comando) e o
    // comando só falhava DEPOIS da confirmação do usuário.
    argsSchema: z
      .object({
        scenarioId: Uuid.optional(),
        lineId: Uuid.optional(),
        objective: SimulationObjectiveSchema,
        targetQuantity: z.number().int().positive().optional(),
        productionOrderId: Uuid.optional(),
        variationPct: z.number().int().min(1).max(25).optional(),
        candidateCount: z
          .union([z.literal(4), z.literal(6), z.literal(8), z.literal(10)])
          .optional(),
      })
      .strict(),
  },
  {
    name: "simulation.cancelBatch",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cancela um lote de simulação automática em andamento.",
    argsSchema: z.object({ batchId: Uuid }).strict(),
  },
];
